import { useState, useMemo, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jsQR from 'jsqr';
import { deployCoversDay } from '@cls/shared';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { downloadCSV } from '../lib/csv.js';
import { localDateTime } from '../lib/localTime.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { SigninCorrectModal } from '../components/domain/SigninCorrectModal.js';
import { SearchableSelect } from '../components/primitives/SearchableSelect.js';

interface Worker {
  id: string;
  name: string;
  role: string;
  status: string;
  companyRef?: string | null;
  photo?: string | null;
  phone?: string | null;
  projects?: string[];
}
interface Project {
  id: string;
  code: string;
  name: string;
  status: string;
  lat?: number | null;
  lng?: number | null;
  geofenceRadiusM?: number | null;
  postcode?: string | null;
  address?: string | null;
}
interface SignInRecord {
  id: string;
  code?: string | null;
  workerId: string;
  workerName: string;
  workerRole?: string | null;
  projectId: string | null;
  projectName: string | null;
  type: 'in' | 'out';
  timestamp: string;
  lat: number | null;
  lng: number | null;
  accuracy?: number | null;
  withinGeofence?: boolean | null;
  distanceFromProject?: number | null;
  nearestProjectId?: string | null;
  nearestProjectName?: string | null;
  nearestProjectDist?: number | null;
  locationAddress?: string | null;
  device?: string | null;
  notes?: string | null;
}

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const STATUS_COL = { idle: 'var(--muted)', warn: 'var(--warn)', ok: 'var(--live)', err: 'var(--danger)' } as const;
type StatusTone = keyof typeof STATUS_COL;

type SortCol = 'id' | 'workerName' | 'projectId' | 'type' | 'timestamp' | 'locationAddress' | 'nearestProjectId' | 'nearestProjectDist' | 'withinGeofence';

export function SignIn() {
  const { canExport, isInternal, isSiteUser } = useRolePerms();
  const { user } = useAuth();
  const isWorker = isSiteUser && !!user?.workerId;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [sortCol, setSortCol] = useState<SortCol | null>('timestamp');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);
  const [worker, setWorker] = useState('');
  const [project, setProject] = useState('');
  const [search, setSearch] = useState('');
  const [fProj, setFProj] = useState('');
  const [fType, setFType] = useState('');
  useResetSub(() => { setSearch(''); setFProj(''); setFType(''); });
  const [correcting, setCorrecting] = useState<SignInRecord | null>(null);
  const [active, setActive] = useState(false);
  const [qrStatus, setQrStatus] = useState<{ text: string; tone: StatusTone }>({ text: 'Scanner inactive', tone: 'idle' });
  const [scannedWorker, setScannedWorker] = useState<Worker | null>(null);
  const [qrProject, setQrProject] = useState('');
  const [qrGpsLine, setQrGpsLine] = useState<string>('');
  const [gpsPos, setGpsPos] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [gpsErr, setGpsErr] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const watchRef = useRef<number | null>(null);
  const lastHitRef = useRef<{ id: string; at: number } | null>(null);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: records = [] } = useQuery({ queryKey: ['signin-records'], queryFn: () => api.get<SignInRecord[]>('/signin-records').then((r) => r.data) });

  const liveProjects = projects.filter((p) => p.status === 'live');

  const projectByIdMap = useMemo(() => {
    const m = new Map<string, Project>();
    projects.forEach((p) => m.set(p.id, p));
    return m;
  }, [projects]);
  const projectLabel = (pid: string | null | undefined): string => {
    if (!pid) return '—';
    const p = projectByIdMap.get(pid);
    return p?.code ?? pid.slice(0, 8);
  };

  const locationDisplay = (r: SignInRecord): string => {
    if (r.locationAddress && r.locationAddress.trim()) return r.locationAddress;
    if (r.nearestProjectName) {
      const np = r.nearestProjectId ? projectByIdMap.get(r.nearestProjectId) : null;
      const addr = (np as any)?.address as string | undefined;
      const parts = (addr ?? '').split(',').map((s: string) => s.trim()).filter(Boolean);
      const city = parts.length > 1 ? parts[parts.length - 1] : null;
      return city ? `Near ${r.nearestProjectName}, ${city}` : `Near ${r.nearestProjectName}`;
    }
    if (r.lat != null && r.lng != null && (r.lat !== 0 || r.lng !== 0)) return `${r.lat.toFixed(4)}, ${r.lng.toFixed(4)}`;
    return '—';
  };

  // Deep link: /signin?worker=<id-or-companyRef>
  useEffect(() => {
    const w = params.get('worker');
    if (!w || !workers.length || worker) return;
    const found = resolveWorker(w);
    if (found) setWorker(found.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, workers]);

  const filtered = useMemo(() => {
    let r = [...records];
    const s = search.toLowerCase().trim();
    if (s) r = r.filter((x) => [
      x.workerName,
      x.workerRole ?? '',
      x.projectName,
      x.projectId,
      projectLabel(x.projectId),
      projectLabel(x.nearestProjectId),
      x.code,
      x.nearestProjectId ?? '',
      x.locationAddress ?? '',
    ].filter(Boolean).join(' ').toLowerCase().includes(s));
    if (fProj) r = r.filter((x) => x.projectId === fProj);
    if (fType) r = r.filter((x) => x.type === fType);
    if (sortCol) {
      r = r.slice().sort((a, b) => {
        const av: any = (a as any)[sortCol] ?? '';
        const bv: any = (b as any)[sortCol] ?? '';
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sortDir;
        return String(av).localeCompare(String(bv)) * sortDir;
      });
    }
    return r;
  }, [records, search, fProj, fType, sortCol, sortDir]);

  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortCol(col); setSortDir(1); }
  };

  const sortArrow = (col: SortCol) => sortCol === col ? (sortDir === 1 ? '↑' : '↓') : '⇅';
  const sortHeaderStyle = { cursor: 'pointer', userSelect: 'none' as const };

  const deleteRecord = useMutation({
    mutationFn: async (id: string) => api.delete(`/signin-records/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signin-records'] });
      qc.invalidateQueries({ queryKey: ['activity-log'] });
      toast.info('Record deleted');
    },
    onError: (e: any) => toast.error('Delete failed', e?.response?.data?.message ?? 'Try again'),
  });

  const onProjectClick = (pid: string | null | undefined) => { if (pid) navigate(`/projects?focus=${pid}`); };
  const onWorkerClick = (wid: string | null | undefined) => { if (wid) navigate(`/workers?focus=${wid}`); };

  const today = new Date().toISOString().slice(0, 10);
  const todayRecords = records.filter((r) => r.timestamp.slice(0, 10) === today);

  function resolveWorker(raw: string): Worker | undefined {
    const token = raw.trim();
    if (!token) return undefined;
    const upper = token.toUpperCase();
    const lower = token.toLowerCase();
    let hit = workers.find((w) => w.companyRef && w.companyRef.toUpperCase() === upper);
    if (!hit) hit = workers.find((w) => w.id === token);
    if (!hit) {
      const uuidLike = token.match(/[0-9a-fA-F-]{8,}/)?.[0];
      if (uuidLike) hit = workers.find((w) => w.id === uuidLike || w.id.startsWith(uuidLike));
    }
    if (!hit) hit = workers.find((w) => w.name.toLowerCase() === lower);
    return hit;
  }

  const sign = useMutation({
    mutationFn: async ({ workerId, projectId, type }: { workerId: string; projectId: string; type: 'in' | 'out' }) => {
      let lat = 0, lng = 0, accuracy = 0;
      try {
        const pos = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000, enableHighAccuracy: true }));
        lat = pos.coords.latitude; lng = pos.coords.longitude; accuracy = pos.coords.accuracy;
      } catch { /* GPS unavailable — still record with zeros */ }
      return api.post<SignInRecord>('/signin-records', {
        workerId,
        projectId,
        type,
        lat,
        lng,
        accuracy,
        device: navigator.userAgent.slice(0, 200),
      }).then((r) => r.data);
    },
    onSuccess: (rec, v) => {
      qc.invalidateQueries({ queryKey: ['signin-records'] });
      const inside = rec?.withinGeofence;
      const tail = inside === true ? '✓ within geofence' : inside === false ? '⚠ outside geofence' : '(no GPS)';
      const tone = inside === false ? 'warn' : 'success';
      const title = (v.type === 'in' ? '✅ Signed in' : '🚪 Signed out');
      if (tone === 'warn') toast.warn(title, tail);
      else toast.success(title, tail);
    },
    onError: () => toast.error('Sign failed', 'Check fields and try again'),
  });

  const handleManual = (type: 'in' | 'out') => {
    if (!worker || !project) { toast.error('Missing', 'Select worker and project'); return; }
    sign.mutate({ workerId: worker, projectId: project, type });
  };

  const stop = () => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (videoRef.current) { videoRef.current.srcObject = null; }
    lastHitRef.current = null;
    setActive(false);
    setQrStatus({ text: 'Scanner stopped', tone: 'idle' });
  };

  const start = async () => {
    if (active) return;
    if (!navigator.mediaDevices?.getUserMedia) { toast.error('Camera unavailable'); setQrStatus({ text: 'Camera not supported', tone: 'err' }); return; }
    setQrStatus({ text: 'Requesting camera…', tone: 'warn' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      const vid = videoRef.current;
      if (!vid) { stream.getTracks().forEach((t) => t.stop()); return; }
      vid.srcObject = stream;
      await vid.play();
      setActive(true);
      setQrStatus({ text: '● Scanning… Point at worker QR code', tone: 'ok' });
      tick();
    } catch (err: any) {
      toast.error('Camera access denied', err?.message ?? 'Allow camera in browser settings');
      setQrStatus({ text: `Camera error: ${err?.message ?? 'blocked'}`, tone: 'err' });
    }
  };

  const tick = () => {
    const vid = videoRef.current;
    const cvs = canvasRef.current;
    if (!vid || !cvs) { rafRef.current = requestAnimationFrame(tick); return; }
    if (vid.readyState >= 2) {
      cvs.width = vid.videoWidth;
      cvs.height = vid.videoHeight;
      const ctx = cvs.getContext('2d');
      if (ctx && cvs.width > 0 && cvs.height > 0) {
        ctx.drawImage(vid, 0, 0, cvs.width, cvs.height);
        const img = ctx.getImageData(0, 0, cvs.width, cvs.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
        if (code?.data) {
          const now = Date.now();
          const last = lastHitRef.current;
          if (!last || last.id !== code.data || now - last.at > 2500) {
            lastHitRef.current = { id: code.data, at: now };
            const hit = resolveWorker(code.data);
            if (hit) {
              onScanSuccess(hit);
              return;
            }
            setQrStatus({ text: `⚠ Worker not found for: "${code.data.slice(0, 32)}" — keep scanning`, tone: 'warn' });
          }
        }
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const onScanSuccess = (w: Worker) => {
    stop();
    setScannedWorker(w);
    setQrStatus({ text: `✓ ${w.name} identified`, tone: 'ok' });
    // Default project: prefer first of worker's deployed live projects
    const wp = liveProjects.filter((p) => (w.projects || []).includes(p.id));
    setQrProject(wp[0]?.id ?? liveProjects[0]?.id ?? '');
    // Show nearest site
    setQrGpsLine('Locating…');
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const candidates = liveProjects.filter((p) => p.lat != null && p.lng != null);
        if (!candidates.length) { setQrGpsLine(''); return; }
        const ranked = candidates.map((p) => ({ p, d: haversineM(pos.coords.latitude, pos.coords.longitude, p.lat as number, p.lng as number) })).sort((a, b) => a.d - b.d);
        const top = ranked[0];
        if (top) setQrGpsLine(`📍 Nearest site: ${top.p.code} — ${top.p.name} (${Math.round(top.d)}m away)`);
        else setQrGpsLine('');
      }, () => setQrGpsLine(''), { timeout: 5000, enableHighAccuracy: true });
    } else {
      setQrGpsLine('');
    }
  };

  const handleQrSign = (type: 'in' | 'out') => {
    if (!scannedWorker) { toast.error('No worker scanned'); return; }
    if (!qrProject) { toast.error('Select a project'); return; }
    sign.mutate({ workerId: scannedWorker.id, projectId: qrProject, type }, {
      onSuccess: () => {
        setScannedWorker(null);
        setQrProject('');
        setQrGpsLine('');
        setQrStatus({ text: 'Signed! Tap Start Camera for next worker', tone: 'ok' });
      },
    });
  };

  // Suggest in/out based on last record for the scanned worker
  const suggestion: 'in' | 'out' | null = useMemo(() => {
    if (!scannedWorker) return null;
    const last = records.find((r) => r.workerId === scannedWorker.id);
    if (!last) return 'in';
    return last.type === 'in' ? 'out' : 'in';
  }, [scannedWorker, records]);

  useEffect(() => () => stop(), []);

  // Live GPS watch when a project is selected
  useEffect(() => {
    if (!project) {
      setGpsPos(null);
      setGpsErr(null);
      if (watchRef.current !== null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; }
      return;
    }
    if (!navigator.geolocation) { setGpsErr('Geolocation unavailable'); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => { setGpsPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); setGpsErr(null); },
      (e) => setGpsErr(e.message),
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 15_000 },
    );
    watchRef.current = id;
    return () => { if (watchRef.current !== null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };
  }, [project]);

  const geofenceLine = useMemo(() => {
    if (!project) return { text: 'Select a project to enable live GPS + geofence status', tone: 'idle' as StatusTone };
    const p = projects.find((x) => x.id === project);
    if (!p) return { text: 'Project not found', tone: 'err' as StatusTone };
    if (gpsErr) return { text: `GPS error: ${gpsErr}`, tone: 'err' as StatusTone };
    if (!gpsPos) return { text: 'Waiting for GPS fix…', tone: 'idle' as StatusTone };
    if (p.lat == null || p.lng == null) return { text: `📍 ${gpsPos.lat.toFixed(4)}, ${gpsPos.lng.toFixed(4)} · project has no coordinates on file`, tone: 'warn' as StatusTone };
    const dist = haversineM(gpsPos.lat, gpsPos.lng, p.lat, p.lng);
    const radius = p.geofenceRadiusM ?? 100;
    const within = dist <= radius;
    return {
      text: `📍 ${dist < 1000 ? Math.round(dist) + 'm' : (dist / 1000).toFixed(1) + 'km'} from ${p.code} · ${within ? '✓ inside geofence' : `⚠ outside ${radius}m geofence`} · accuracy ±${Math.round(gpsPos.accuracy)}m`,
      tone: within ? 'ok' as StatusTone : 'warn' as StatusTone,
    };
  }, [project, projects, gpsPos, gpsErr]);

  // ── Worker self-service mode ────────────────────────────────────────────
  // For Worker role, we replace the whole admin UI with a single-card
  // self-service flow: their name + a site picker + Sign In / Sign Out
  // buttons. No records table.
  const myWorker = isWorker && user?.workerId ? workers.find((w) => w.id === user.workerId) : null;
  const myDeployments = useQuery({
    queryKey: ['deployments', 'mine'],
    queryFn: () => api.get<{ id: string; projectId: string; projectCode: string | null; projectName: string | null; startDate: string; endDate: string | null; status: string }[]>('/deployments').then((r) => r.data),
    enabled: !!isWorker && !!user?.workerId,
  });
  const myTodayDeploys = useMemo(() => {
    if (!isWorker || !myDeployments.data) return [];
    return myDeployments.data
      .filter((d) => deployCoversDay(d, today, today))
      .sort((a, b) => b.startDate.localeCompare(a.startDate));
  }, [isWorker, myDeployments.data, today]);
  const myCurrentDeploy = myTodayDeploys[0] ?? null;
  const { data: liveSites = [] } = useQuery({
    queryKey: ['projects', 'live-sites'],
    queryFn: () => api.get<{ id: string; code: string; name: string }[]>('/projects/live-sites').then((r) => r.data),
    enabled: !!isWorker,
  });
  const [mySite, setMySite] = useState('');
  const myOwnSites = useMemo(
    () => myTodayDeploys.filter((d, i, all) => all.findIndex((x) => x.projectId === d.projectId) === i),
    [myTodayDeploys],
  );
  const myOtherSites = useMemo(() => {
    const own = new Set(myOwnSites.map((d) => d.projectId));
    return liveSites.filter((p) => !own.has(p.id));
  }, [myOwnSites, liveSites]);
  const mySiteId = mySite || myCurrentDeploy?.projectId || '';
  const mySignToday: 'in' | 'out' | null = useMemo(() => {
    if (!isWorker || !user?.workerId) return null;
    const last = records.find((r) => r.workerId === user.workerId);
    if (!last) return 'in';
    return last.type === 'in' ? 'out' : 'in';
  }, [isWorker, user?.workerId, records]);

  // QR-result project optgroups: worker's live projects first, then other live
  const qrWorkerProjects = useMemo(() => {
    if (!scannedWorker) return [] as Project[];
    return liveProjects.filter((p) => (scannedWorker.projects || []).includes(p.id));
  }, [scannedWorker, liveProjects]);
  const qrOtherProjects = useMemo(() => {
    if (!scannedWorker) return liveProjects;
    const own = new Set((scannedWorker.projects || []));
    return liveProjects.filter((p) => !own.has(p.id));
  }, [scannedWorker, liveProjects]);

  if (isWorker) {
    const hasDeploy = !!myCurrentDeploy;
    const projectLine = hasDeploy
      ? `${myCurrentDeploy!.projectCode ?? myCurrentDeploy!.projectId.slice(0, 8)} — ${myCurrentDeploy!.projectName ?? ''}`
      : 'Not currently deployed';
    return (
      <div className="page" id="page-signin">
        <div className="hbar">
          <div>
            <div className="ph">✅ Sign In / Out</div>
            <div className="ps">Tap the button when you arrive on site and again when you leave.</div>
          </div>
        </div>
        <div className="card" style={{ maxWidth: 480, margin: '20px auto', padding: 22, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontFamily: 'Syne, sans-serif', fontWeight: 700, marginBottom: 6 }}>
            {myWorker?.name ?? user?.name ?? 'Worker'}
          </div>
          <div style={{ fontSize: 13, color: hasDeploy ? 'var(--accent)' : 'var(--danger)', marginBottom: 18, fontWeight: 600 }}>
            {projectLine}
          </div>
          {hasDeploy ? (
            <div className="form-row" style={{ marginBottom: 14, textAlign: 'left' }}>
              <label className="form-label">Site</label>
              <select className="form-select" id="si-my-project" value={mySiteId} onChange={(e) => setMySite(e.target.value)}>
                <optgroup label="My Projects">
                  {myOwnSites.map((d) => <option key={d.projectId} value={d.projectId}>{d.projectCode ?? d.projectId.slice(0, 8)} — {d.projectName ?? ''}</option>)}
                </optgroup>
                {myOtherSites.length > 0 ? (
                  <optgroup label="Other Live Projects">
                    {myOtherSites.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                  </optgroup>
                ) : null}
              </select>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              className="btn btn-primary"
              style={{ flex: 1, justifyContent: 'center', padding: 14, fontSize: 14, ...(mySignToday === 'in' ? { outline: '2px solid var(--live)' } : null) }}
              disabled={!hasDeploy || sign.isPending}
              title={hasDeploy ? '' : 'No active deployment — speak to the office.'}
              onClick={() => sign.mutate({ workerId: user!.workerId!, projectId: mySiteId, type: 'in' })}
            >
              ✅ Sign In
            </button>
            <button
              className="btn btn-ghost"
              style={{ flex: 1, justifyContent: 'center', padding: 14, fontSize: 14, ...(mySignToday === 'out' ? { outline: '2px solid var(--warn)' } : null) }}
              disabled={!hasDeploy || sign.isPending}
              onClick={() => sign.mutate({ workerId: user!.workerId!, projectId: mySiteId, type: 'out' })}
            >
              🚪 Sign Out
            </button>
          </div>
          {!hasDeploy ? (
            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--muted)' }}>
              You can only sign in to a site you've been deployed to. If you're meant to be on site, contact the office.
            </div>
          ) : (
            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--muted)' }}>
              Suggestion: {mySignToday === 'in' ? 'Sign In' : 'Sign Out'} (based on your last record).
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page" id="page-signin">
      <div className="hbar">
        <div>
          <div className="ph">✅ Site Sign In / Out</div>
          <div className="ps">Worker attendance with GPS geofencing</div>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV('signin-records.csv', filtered.map((r) => ({
            ref: r.code ?? r.id,
            worker: r.workerName,
            project: r.projectName ?? r.projectId ?? '',
            type: r.type,
            date: localDateTime(r.timestamp).date,
            time: localDateTime(r.timestamp).time,
            lat: r.lat ?? '',
            lng: r.lng ?? '',
            accuracy: r.accuracy ?? '',
            location: locationDisplay(r),
            nearestSite: r.nearestProjectId ?? '',
            distance: r.nearestProjectDist ?? '',
            geofence: r.withinGeofence == null ? '' : r.withinGeofence ? 'yes' : 'no',
          })))}>⬇ Export CSV</button> : null}
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => { window.open('/api/reports/signin/pdf', '_blank'); toast.info('Export started', 'Sign-In log · PDF'); }}>⬇ PDF Report</button> : null}
        </div>
      </div>

      <div className="g2" style={{ marginBottom: 14 }}>
        {/* QR SCANNER PANEL */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="sec-title" style={{ margin: 0 }}>📷 QR Scan Sign In/Out</div>
            <div style={{ display: 'flex', gap: 5 }}>
              <button
                className="btn btn-primary btn-sm"
                id="si-qr-start-btn"
                style={{ display: active ? 'none' : undefined }}
                onClick={start}
              >📷 Start Camera</button>
              <button
                className="btn btn-ghost btn-sm"
                id="si-qr-stop-btn"
                style={{ display: active ? undefined : 'none' }}
                onClick={stop}
              >⏹ Stop</button>
            </div>
          </div>
          <div id="si-qr-wrap" style={{ background: '#000', borderRadius: 10, overflow: 'hidden', position: 'relative', aspectRatio: '4 / 3', maxHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
            <video
              ref={videoRef}
              id="si-qr-video"
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: active ? 'block' : 'none' }}
            />
            <canvas ref={canvasRef} id="si-qr-canvas" style={{ display: 'none' }} />
            {!active ? (
              <div id="si-qr-placeholder" style={{ color: '#666', textAlign: 'center', padding: 20, position: 'absolute' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📷</div>
                <div style={{ fontSize: 12 }}>Start camera to scan worker QR code</div>
              </div>
            ) : (
              <div id="si-qr-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 180, height: 180, border: '3px solid var(--accent)', borderRadius: 10, boxShadow: '0 0 0 2000px rgba(0,0,0,.45)' }} />
              </div>
            )}
          </div>
          <div id="si-qr-status" style={{ textAlign: 'center', fontSize: 11, color: STATUS_COL[qrStatus.tone], marginBottom: 10 }}>
            {qrStatus.text}
          </div>

          {scannedWorker ? (
            <div id="si-qr-result" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 9, padding: 12 }}>
              <div id="si-qr-worker-info" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {scannedWorker.photo ? (
                    <img src={scannedWorker.photo} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--live)' }} />
                  ) : (
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: '#000' }}>
                      {scannedWorker.name.split(' ').map((n) => n[0] ?? '').join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--live)' }}>✓ {scannedWorker.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{scannedWorker.role} · {scannedWorker.companyRef || `ID:${scannedWorker.id.slice(0, 8)}`}</div>
                    <div style={{ fontSize: 10, color: 'var(--faint)' }}>{scannedWorker.status}</div>
                  </div>
                </div>
              </div>
              <div className="form-row" style={{ marginBottom: 8 }}>
                <label className="form-label">Project</label>
                <select className="form-select" id="si-qr-project" value={qrProject} onChange={(e) => setQrProject(e.target.value)}>
                  {qrWorkerProjects.length > 0 ? (
                    <optgroup label="Worker's Projects">
                      {qrWorkerProjects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                    </optgroup>
                  ) : null}
                  {qrOtherProjects.length > 0 ? (
                    <optgroup label="Other Live Projects">
                      {qrOtherProjects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                    </optgroup>
                  ) : null}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 7 }}>
                {isInternal ? <>
                <button
                  className="btn btn-primary"
                  style={{ flex: 1, justifyContent: 'center', padding: 10, ...(suggestion === 'in' ? { outline: '2px solid var(--live)' } : null) }}
                  onClick={() => handleQrSign('in')}
                  disabled={sign.isPending}
                >✅ Sign In</button>
                <button
                  className="btn btn-ghost"
                  style={{ flex: 1, justifyContent: 'center', padding: 10, ...(suggestion === 'out' ? { outline: '2px solid var(--warn)' } : null) }}
                  onClick={() => handleQrSign('out')}
                  disabled={sign.isPending}
                >🚪 Sign Out</button>
                </> : null}
              </div>
              <div id="si-qr-gps" style={{ marginTop: 7, fontSize: 10, color: 'var(--muted)' }}>{qrGpsLine}</div>
            </div>
          ) : null}
        </div>

        {/* MANUAL + TODAY */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isInternal ? <div className="card">
            <div className="sec-title" style={{ marginBottom: 10 }}>Manual Sign In / Out</div>
            <div className="form-row">
              <label className="form-label">Worker</label>
              <SearchableSelect
                id="si-worker"
                value={worker}
                onChange={setWorker}
                placeholder="— Select worker —"
                options={[
                  { value: '', label: '— Select worker —' },
                  ...workers.map((w) => ({ value: w.id, label: `${w.name}${w.companyRef ? ` (${w.companyRef})` : ''}` })),
                ]}
              />
            </div>
            <div className="form-row">
              <label className="form-label">Project</label>
              <SearchableSelect
                id="si-project"
                value={project}
                onChange={setProject}
                placeholder="— Select project —"
                options={[
                  { value: '', label: '— Select project —' },
                  ...liveProjects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` })),
                ]}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handleManual('in')} disabled={sign.isPending}>✅ Sign In</button>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handleManual('out')} disabled={sign.isPending}>🚪 Sign Out</button>
            </div>
            <div id="si-gps-status" style={{ marginTop: 8, fontSize: 10, color: STATUS_COL[geofenceLine.tone] }}>{geofenceLine.text}</div>
          </div> : null}
          <div className="card" style={{ flex: 1 }}>
            <div className="sec-title" style={{ marginBottom: 10 }}>Today's Activity</div>
            <div id="si-today">
              {todayRecords.length === 0 ? (
                <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 20 }}>No sign in/out records today</div>
              ) : todayRecords.slice(0, 8).map((r) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid rgba(30,42,64,.4)' }}>
                  <span style={{ fontSize: 16 }}>{r.type === 'in' ? '✅' : '🚪'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{r.workerName}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{projectLabel(r.projectId)} · {new Date(r.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                  {r.withinGeofence === true ? <span className="geo-ok" style={{ fontSize: 10 }}>✓</span> : r.withinGeofence === false ? <span className="geo-warn" style={{ fontSize: 10 }}>⚠</span> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* RECORDS TABLE */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div className="sec-title" style={{ margin: 0 }}>Sign In/Out Records</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 360, maxWidth: '100%' }}>
            <div className="tbl-search" style={{ width: '100%', margin: 0, padding: '8px 12px' }}>
              <span style={{ color: 'var(--faint)' }}>⌕</span>
              <input id="si-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ fontSize: 13 }} />
            </div>
            <SearchableSelect
              id="si-filter-proj"
              value={fProj}
              onChange={setFProj}
              placeholder="All Projects"
              options={[
                { value: '', label: 'All Projects' },
                ...liveProjects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` })),
              ]}
            />
            <select className="form-select" id="si-filter-type" value={fType} onChange={(e) => setFType(e.target.value)} style={{ padding: '8px 12px', fontSize: 13 }}>
              <option value="">All</option>
              <option value="in">Sign In</option>
              <option value="out">Sign Out</option>
            </select>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead id="si-thead">
              <tr>
                <th onClick={() => handleSort('id')} style={sortHeaderStyle}>Ref <span style={{ opacity: sortCol === 'id' ? 1 : 0.3 }}>{sortArrow('id')}</span></th>
                <th onClick={() => handleSort('workerName')} style={sortHeaderStyle}>Worker <span style={{ opacity: sortCol === 'workerName' ? 1 : 0.3 }}>{sortArrow('workerName')}</span></th>
                <th onClick={() => handleSort('projectId')} style={sortHeaderStyle}>Project <span style={{ opacity: sortCol === 'projectId' ? 1 : 0.3 }}>{sortArrow('projectId')}</span></th>
                <th onClick={() => handleSort('type')} style={sortHeaderStyle}>Type <span style={{ opacity: sortCol === 'type' ? 1 : 0.3 }}>{sortArrow('type')}</span></th>
                <th onClick={() => handleSort('timestamp')} style={sortHeaderStyle}>Date <span style={{ opacity: sortCol === 'timestamp' ? 1 : 0.3 }}>{sortArrow('timestamp')}</span></th>
                <th onClick={() => handleSort('timestamp')} style={sortHeaderStyle}>Time <span style={{ opacity: sortCol === 'timestamp' ? 1 : 0.3 }}>{sortArrow('timestamp')}</span></th>
                <th onClick={() => handleSort('locationAddress')} style={sortHeaderStyle}>Location <span style={{ opacity: sortCol === 'locationAddress' ? 1 : 0.3 }}>{sortArrow('locationAddress')}</span></th>
                <th onClick={() => handleSort('nearestProjectId')} style={sortHeaderStyle}>Nearest Site <span style={{ opacity: sortCol === 'nearestProjectId' ? 1 : 0.3 }}>{sortArrow('nearestProjectId')}</span></th>
                <th onClick={() => handleSort('nearestProjectDist')} style={sortHeaderStyle}>Distance <span style={{ opacity: sortCol === 'nearestProjectDist' ? 1 : 0.3 }}>{sortArrow('nearestProjectDist')}</span></th>
                <th onClick={() => handleSort('withinGeofence')} style={sortHeaderStyle}>Geofence <span style={{ opacity: sortCol === 'withinGeofence' ? 1 : 0.3 }}>{sortArrow('withinGeofence')}</span></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody id="si-tbody">
              {filtered.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}>No records found</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontFamily: 'Syne, sans-serif', fontSize: 9, color: 'var(--faint)' }}>{r.code ?? r.id.slice(0, 10)}</td>
                  <td style={{ cursor: 'pointer' }} onClick={() => onWorkerClick(r.workerId)} title="Open worker">
                    <strong>{r.workerName}</strong>
                    {r.workerRole ? <div style={{ fontSize: 9, color: 'var(--muted)' }}>{r.workerRole}</div> : null}
                  </td>
                  <td style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => onProjectClick(r.projectId)} title="Open project">
                    {projectLabel(r.projectId)}
                  </td>
                  <td>
                    {r.type === 'in'
                      ? <span className="badge b-active">✅ IN</span>
                      : <span className="badge b-dep-done">🚪 OUT</span>}
                  </td>
                  <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(r.timestamp).toLocaleDateString('en-GB')}</td>
                  <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(r.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                  <td style={{ fontSize: 10, color: 'var(--muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={locationDisplay(r)}>
                    {locationDisplay(r)}
                  </td>
                  <td style={{ fontSize: 10, cursor: r.nearestProjectId ? 'pointer' : 'default', color: r.nearestProjectId ? 'var(--accent)' : 'var(--muted)' }} onClick={() => onProjectClick(r.nearestProjectId)} title={r.nearestProjectId ? 'Open project' : ''}>
                    {projectLabel(r.nearestProjectId)}
                    {r.nearestProjectName ? <div style={{ fontSize: 9, color: 'var(--faint)' }}>{r.nearestProjectName}</div> : null}
                  </td>
                  <td style={{ fontSize: 11, textAlign: 'center' }}>
                    {r.nearestProjectDist != null
                      ? `${Math.round(r.nearestProjectDist)}m`
                      : r.distanceFromProject != null ? `${Math.round(r.distanceFromProject)}m` : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {r.withinGeofence === true
                      ? <span className="geo-ok">✓</span>
                      : r.withinGeofence === false
                        ? <span className="geo-warn">⚠ Outside</span>
                        : <span style={{ color: 'var(--faint)' }}>—</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {isInternal ? (
                      <button className="btn btn-ghost btn-xs" onClick={() => setCorrecting(r)} title="Correct record (audit logged)" style={{ padding: '1px 6px', marginRight: 4 }}>✏</button>
                    ) : null}
                    {isInternal ? <button className="btn btn-danger btn-xs" onClick={() => { if (confirm('Delete this record? This is logged for audit.')) deleteRecord.mutate(r.id); }} title="Delete record" style={{ padding: '1px 6px' }} disabled={deleteRecord.isPending}>✕</button> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {correcting ? <SigninCorrectModal onClose={() => setCorrecting(null)} record={correcting as any} /> : null}
    </div>
  );
}
